import { TreatyProgram_treatyProgram } from "_graphql/queries/__generated__/TreatyProgram";
import _ from "lodash";
import { FC } from "react";
import {
  InformationCircleIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";

// Define a type for the offer details structure if known, otherwise use any
type OfferDetail = {
  keydetail?: string | null;
  type?: string | null;
  required?: boolean | string | null; // Assuming it might be boolean or string like "Yes"/"No"
};

// Reusable component for displaying label-value pairs
const DetailItem: FC<{ label: string; value: string | React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value || "N/A"}</dd>
  </div>
);

type Props = {
  program: TreatyProgram_treatyProgram | null | undefined;
};

const ProgramView: FC<Props> = ({ program }) => {
  let offerDetails: OfferDetail[] = [];
  try {
    // Safely parse JSON, default to empty array on error or null/undefined input
    offerDetails = program?.treaty_details
      ? JSON.parse(program.treaty_details)
      : [];
    if (!Array.isArray(offerDetails)) {
      // Ensure it's an array even if parsing results in a non-array object
      console.warn("Parsed treaty_details is not an array:", offerDetails);
      offerDetails = [];
    }
  } catch (error) {
    offerDetails = []; // Ensure it's an empty array on parsing error
  }

  const formatTreatyType = (type: string | null | undefined) => {
    if (!type) return "N/A";
    return _.startCase(type.toLowerCase());
  };

  const formatRequired = (required: boolean | string | null | undefined) => {
    if (required === true || String(required).toLowerCase() === "yes") {
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
          Yes
        </span>
      );
    }
    if (required === false || String(required).toLowerCase() === "no") {
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
          No
        </span>
      );
    }
    // Default or fallback if value is unexpected
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
        Unknown
      </span>
    );
  };

  return (
    <div className="space-y-8">
      {/* Offer Information Section */}
      <section>
        <h3 className="text-lg font-semibold leading-6 text-gray-900 flex items-center">
          <InformationCircleIcon
            className="h-5 w-5 mr-2 text-gray-500"
            aria-hidden="true"
          />
          Offer Information
        </h3>
        <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <DetailItem
              label="Cedant Name"
              value={program?.insurer?.insurer_company_name}
            />
          </div>
          <div className="sm:col-span-1">
            <DetailItem
              label="Treaty Type"
              value={formatTreatyType(program?.treaty_type)}
            />
          </div>
          <div className="sm:col-span-3">
            <DetailItem label="Program Name" value={program?.treaty_name} />
          </div>
        </dl>
      </section>

      {/* Business Class Details Section */}
      <section className="pt-8 border-t border-gray-200">
        <h3 className="text-lg font-semibold leading-6 text-gray-900 flex items-center">
          <TableCellsIcon
            className="h-5 w-5 mr-2 text-gray-500"
            aria-hidden="true"
          />
          Business Class Details
        </h3>
        <div className="mt-4 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              {offerDetails.length > 0 ? (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                        >
                          Detail
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Required
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {offerDetails.map((detail, idx) => (
                        <tr
                          key={idx}
                          className={clsx(
                            idx % 2 === 0 ? undefined : "bg-gray-50"
                          )}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {detail?.keydetail || "N/A"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {detail?.type || "Text"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {formatRequired(detail?.required)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-10 px-4 border border-dashed border-gray-300 rounded-lg">
                  <TableCellsIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No Business Class Details
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    There are no specific business class details defined for
                    this program.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramView;
